import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallbackRoute?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary - Catches JavaScript errors in child component tree
 * Prevents white screen crashes by displaying a friendly error message
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });
    
    // Log error to console
    console.error('🚨 ErrorBoundary caught an error:', error);
    console.error('📍 Component stack:', errorInfo.componentStack);
    
    // TODO: Send to Sentry when configured
    // if (typeof Sentry !== 'undefined') {
    //   Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
    // }
  }

  handleRefresh = (): void => {
    window.location.reload();
  };

  handleGoBack = (): void => {
    const { fallbackRoute } = this.props;
    window.history.pushState({}, '', fallbackRoute || '?view=login');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  render(): ReactNode {
    const { hasError } = this.state;
    const { children } = this.props;

    if (hasError) {
      // Use plain HTML with inline styles - no external CSS dependencies
      // This ensures the error page displays even if stylesheets fail to load
      return (
        <div
          style={{
            minHeight: '100vh',
            backgroundColor: '#f3f4f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
              maxWidth: '400px',
              width: '100%',
              padding: '32px',
              textAlign: 'center',
            }}
          >
            {/* Logo */}
            <div style={{ marginBottom: '24px' }}>
              <img
                src="/vs-logo.svg"
                alt="VeraScore Logo"
                style={{ height: '48px', margin: '0 auto 16px' }}
                onError={(e) => {
                  // Hide image if logo fails to load
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
              <h1
                style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: '#dc2626',
                  margin: 0,
                }}
              >
                Something went wrong
              </h1>
            </div>

            {/* Message */}
            <p
              style={{
                color: '#6b7280',
                marginBottom: '32px',
                lineHeight: 1.5,
              }}
            >
              We're sorry, but something unexpected happened.
              Please try refreshing the page.
            </p>

            {/* Actions */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button
                onClick={this.handleRefresh}
                style={{
                  width: '100%',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  fontWeight: 500,
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '16px',
                }}
              >
                Refresh Page
              </button>

              <button
                onClick={this.handleGoBack}
                style={{
                  width: '100%',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontWeight: 500,
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  cursor: 'pointer',
                  fontSize: '16px',
                }}
              >
                Back to Login
              </button>
            </div>

            {/* Support notice */}
            <p
              style={{
                fontSize: '14px',
                color: '#9ca3af',
                marginTop: '24px',
              }}
            >
              If this problem persists, please contact support.
            </p>
          </div>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;

